import { Button } from '@/components/Button';
import { Text } from '@/components/Text';
import { TextInput } from '@/components/TextInput';
import { useSubscription } from '@/hooks/useSubscription';
import { IconEmojiPicker } from '@/partials/dashboard/Cards/UpdateCard/UpdateCardLinks/IconEmojiPicker';
import { LinkIcon } from '@/partials/dashboard/Cards/UpdateCard/UpdateCardLinks/LinkIcon';
import { RenderLinkFields } from '@/partials/dashboard/Cards/UpdateCard/UpdateCardLinks/RenderLinkFields';
import { useCardStore } from '@/stores/useCardStore';
import { CardLinkFormValues } from '@/types/cardLink';
import { Link } from '@/types/link';
import { handleError } from '@/utils/handleError';
import { Form, Formik, FormikHelpers } from 'formik';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IoArrowBack } from 'react-icons/io5';
import * as Yup from 'yup';

export interface IconDetailViewProps {
  link: Link;
  onBack: () => void;
  onClose: () => void;
}

/**
 * Component to show the detailed view of an icon with form to create a new link
 */
export const IconDetailView = ({
  link,
  onBack,
  onClose,
}: IconDetailViewProps) => {
  const { t } = useTranslation();
  const { card, createCardLink } = useCardStore();
  const [isCreating, setIsCreating] = useState(false);
  const { hasActiveSubscription } = useSubscription(card?.user || null);
  // Initial values for the form
  const initialValues: CardLinkFormValues = {
    title: t(`icon:${link.icon}`),
    url: '',
    icon: link.icon === 'custom_link' ? '😀' : link.icon,
    type: link.icon === 'custom_link' ? 'custom_link' : 'link',
    link_id: link.id,
    is_active: true,
  };

  const linkSchema = Yup.object().shape({
    url: Yup.string().required(t('common:validation.required')),
  });

  // Handle form submission
  const handleSubmit = async (
    values: CardLinkFormValues,
    { resetForm, setErrors }: FormikHelpers<CardLinkFormValues>,
  ) => {
    if (!card) return;
    setIsCreating(true);

    try {
      await createCardLink(
        card.slug,
        values,
        link.icon === 'custom_link' ? true : false,
      );
      resetForm();
      onClose();
    } catch (error) {
      handleError(error, setErrors);
      setIsCreating(false);
    }
  };

  return (
    <div className="flex flex-col space-y-4">
      <div className="mb-2 flex w-full items-center justify-between">
        <button
          type="button"
          onClick={onBack}
          className="w-fit bg-transparent p-0 text-gray-600 shadow-none hover:text-gray-900"
        >
          <IoArrowBack size={20} />
        </button>
        <Text className="text-lg font-medium">
          {t('updateCard:links.add_link', { type: t(`icon:${link.icon}`) })}
        </Text>
      </div>

      <Formik
        initialValues={initialValues}
        validationSchema={linkSchema}
        onSubmit={handleSubmit}
      >
        {({ isValid, dirty, isSubmitting }) => (
          <Form className="flex flex-col p-2">
            {/* Show emoji picker button if custom_link, but always render icon via LinkIcon for consistency */}
            {link.icon === 'custom_link' ? (
              <IconEmojiPicker />
            ) : (
              <LinkIcon
                link={link}
                className="mb-4 size-16"
                iconStyle="size-14 rounded-[16px] aspect-square shadow-sm"
                emojiStyle="text-4xl size-14 rounded-[16px] aspect-square"
              />
            )}

            <div className="mb-4">
              <TextInput
                type="text"
                disabled={!hasActiveSubscription()}
                name="title"
                label={t('updateCard:links.title')}
                placeholder={t('updateCard:links.title_placeholder')}
              />
            </div>

            <RenderLinkFields icon={link.icon} type={link.type} t={t} />

            <Button
              type="submit"
              className="mt-4"
              disabled={!isValid || !dirty || isSubmitting}
              loading={isCreating}
            >
              {t('common:create')}
            </Button>
          </Form>
        )}
      </Formik>
    </div>
  );
};
